import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

interface RequireAdminProps {
  children: React.ReactNode;
  onNavigate?: (screen: string) => void;
}

const RequireAdmin: React.FC<RequireAdminProps> = ({ children, onNavigate }) => {
  const { isSignedIn, user, isLoaded } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [shouldRedirect, setShouldRedirect] = useState<{ to: string; reason: string } | null>(null);
  const navigate = useNavigate();
  const hasCheckedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  // CB:BEGIN test-mode-bypass
  // Check test mode - MUST declare before any returns
  const isTestMode = import.meta.env.VITE_ADMIN_TEST_BYPASS === 'true' ||
                     import.meta.env.VITE_DEMO_MODE === 'true' ||
                     window.location.hostname === 'localhost';
  // CB:END test-mode-bypass

  useEffect(() => {
    if (isTestMode) return; // Skip check in test mode
    if (!isLoaded) return;

    if (!isSignedIn) {
      setShouldRedirect({ to: 'sign-in', reason: 'Not signed in' });
      return;
    }

    // Only check once per user
    if (lastUserIdRef.current !== user?.id) {
      lastUserIdRef.current = user?.id || null;
      hasCheckedRef.current = false;
    }

    if (hasCheckedRef.current) return;

    const checkAdminRole = async () => {
      try {
        const { getApiUrl } = await import('../../lib/getApiUrl');
        const response = await fetch(getApiUrl('/api/me'));
        if (response.ok) {
          const userData = await response.json();
          const isAdmin = userData?.role === 'ADMIN';
          if (isAdmin) {
            setAllowed(true);
          } else {
            setShouldRedirect({ to: 'landing', reason: 'Not an admin' });
          }
        } else {
          setShouldRedirect({ to: 'landing', reason: 'API error' });
        }
      } catch (error) {
        console.error('Error checking admin role:', error);
        setShouldRedirect({ to: 'landing', reason: 'API error' });
      } finally {
        hasCheckedRef.current = true;
      }
    };

    checkAdminRole();
  }, [isLoaded, isSignedIn, user?.id]);

  useEffect(() => {
    if (isTestMode) return; // Skip redirect in test mode
    if (shouldRedirect) {
      if (onNavigate) {
        onNavigate(shouldRedirect.to);
      } else {
        navigate(`/${shouldRedirect.to}`);
      }
    }
  }, [isTestMode, shouldRedirect, onNavigate, navigate]);

  // Test mode bypass - allow access without checks
  if (isTestMode) {
    return <>{children}</>;
  }

  if (shouldRedirect) {
    return <div className="p-8">Redirecting to {shouldRedirect.reason}...</div>;
  }

  if (allowed === null) {
    return <div className="p-8">Checking access…</div>;
  }

  return <>{children}</>;
};

export default RequireAdmin;